'use client';

import { Link } from '@/i18n/navigation';
import { usePaddleCheckout } from '@/features/billing/model/PaddleCheckoutProvider';
import { useAuth } from '@/shared/providers/AuthProvider';

const ACTIVE_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'paused',
];
const LEGACY_PADDLE_ENTERPRISE_MONTHLY_PRICE = 159;

export const PaddleEnterpriseCard = () => {
  const { user } = useAuth();
  const { subscription, subscriptionError } = usePaddleCheckout();
  const isExistingEnterpriseSubscription =
    subscription?.tier === 'enterprise' &&
    ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status);
  const hasPaymentIssue =
    subscription?.status === 'past_due' || subscription?.status === 'paused';
  const renderAction = () => {
    if (!user) {
      return (
        <Link
          className="flex h-[50px] w-full items-center justify-center rounded-[12px] border border-gray-900 text-body1 font-[600] text-gray-900"
          href="/inquiry?pageType=enterprise"
        >
          Contact Sales
        </Link>
      );
    }

    if (subscriptionError) {
      return (
        <button
          type="button"
          disabled
          className="text-body1 flex h-[50px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 px-[12px] font-[600] text-gray-600"
        >
          Unable to verify subscription. Refresh to try again.
        </button>
      );
    }

    if (subscription === undefined) {
      return (
        <button
          type="button"
          disabled
          className="text-body1 flex h-[50px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 font-[600] text-gray-600"
        >
          Checking subscription...
        </button>
      );
    }

    if (isExistingEnterpriseSubscription) {
      return (
        <button
          type="button"
          disabled
          className="text-body1 flex h-[50px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 font-[600] text-gray-600"
        >
          {hasPaymentIssue ? 'Payment required' : 'Current plan'}
        </button>
      );
    }

    return (
      <Link
        className="flex h-[50px] w-full items-center justify-center rounded-[12px] border border-gray-900 text-body1 font-[600] text-gray-900"
        href="/inquiry?pageType=enterprise"
      >
        Contact Sales
      </Link>
    );
  };

  return (
    <article className="flex min-h-[360px] flex-col rounded-[20px] border border-gray-900 bg-white p-[28px] shadow-sm">
      <h2 className="text-title1 font-[800]">Enterprise</h2>
      <div className="mt-[18px] mb-[24px]">
        {isExistingEnterpriseSubscription ? (
          <div className="flex items-end gap-[4px]">
            <span className="text-heading1 font-[800]">
              ${LEGACY_PADDLE_ENTERPRISE_MONTHLY_PRICE}
            </span>
            <span className="text-body1 pb-[6px] text-gray-500">/ month</span>
          </div>
        ) : (
          <span className="text-heading3 font-[800]">Custom pricing</span>
        )}
      </div>
      <p className="text-body2 mb-[18px] font-[600] text-gray-900">
        {isExistingEnterpriseSubscription
          ? 'Legacy Paddle subscription'
          : 'Built around your organization’s requirements'}
      </p>
      <p className="text-body1 mb-[32px] flex-1 leading-[24px] text-gray-500">
        {isExistingEnterpriseSubscription
          ? `Renews monthly at $${LEGACY_PADDLE_ENTERPRISE_MONTHLY_PRICE} until canceled.`
          : 'Custom AI agents, privacy review for uploaded documents, tailored capacity, deployment options, and dedicated support for your business.'}
      </p>
      {renderAction()}
    </article>
  );
};
